<template>
  <AM3DetailCard
    class="mt-4"
    title="Messaging"
    data-test-id="profile-message-list"
    @link-click="handleManageListSubscriptions"
  >
    <!-- All-time total across both channels (matches the activity feed) -->
    <div class="rounded-md bg-surfaces-neutral p-4 mb-4">
      <div class="flex flex-row items-center justify-between mb-3">
        <h3 class="font-medium leading-tight">
          Total messages
          <span class="block text-secondary text-xs font-normal mt-0.5">All time</span>
        </h3>
      </div>
      <div class="flex flex-row items-start justify-between mb-4">
        <div class="flex-shrink basis-[110px] pr-4 text-secondary w-28">
          Sent
        </div>
        <div
          v-if="isLoadingTotals"
          class="flex-1 h-8"
        >
          <AM3LoadingContent :lines="1" />
        </div>
        <div
          v-else
          class="flex-1"
          data-test-id="profile-message-total"
        >
          <div>
            {{ customerTotals?.totalMessagesSent || 0 }} {{ customerTotals?.totalMessagesSent === 1 ? 'message' : 'messages' }}
          </div>
          <div
            v-tooltip.bottom="customerTotals?.totalMessagesSent && customerTotals?.lastMessageSent ? formatDateTime(customerTotals.lastMessageSent) : null"
            class="text-secondary text-xs"
          >
            <template v-if="customerTotals?.totalMessagesSent">
              Latest activity {{ dayjs(customerTotals.lastMessageSent).fromNow() }}
            </template>
            <template v-else>
              No messages sent
            </template>
          </div>
        </div>
      </div>
      <div class="flex flex-row items-start justify-between">
        <div class="flex-shrink basis-[110px] pr-4 text-secondary w-28">
          Engagement
        </div>
        <div
          v-if="isLoadingTotals"
          class="flex-1 h-8"
        >
          <AM3LoadingContent :lines="1" />
        </div>
        <div
          v-else
          class="flex-1"
        >
          <div>
            {{ combinedClickRate }}
          </div>
        </div>
      </div>
    </div>

    <!-- Email Stats -->
    <div
      class="rounded-md bg-surfaces-neutral p-4 mb-4 [&_[data-component=am3-tag]]:outline-0 "
    >
      <div class="flex flex-row items-center justify-between mb-3">
        <h3 class="font-medium leading-tight">
          Email
          <span class="block text-secondary text-xs font-normal mt-0.5">Last {{ customerState?.summaryPeriodDays || '90' }} days</span>
        </h3>
        <AM3Tag
          :type="getSubscriptionType(emailSubscribed)"
          :label="getSubscriptionLabel(emailSubscribed)"
          :dot="true"
        />
      </div>
      <div class="flex flex-row items-start justify-between mb-4">
        <div class="flex-shrink basis-[110px] pr-4 text-secondary w-28 ">
          Sent
        </div>

        <div
          v-if="isLoading"
          class="flex-1 h-8"
        >
          <AM3LoadingContent :lines="1" />
        </div>

        <div
          v-else
          class="flex-1"
        >
          <div>
            {{ customerState?.emailMessagesSent || 0 }} {{ customerState?.emailMessagesSent === 1 ? 'email' : 'emails' }}
          </div>
          <div
            v-tooltip.bottom="customerState?.emailMessagesSent && customerState?.emailLastSentTimestamp ? formatDateTime(customerState.emailLastSentTimestamp) : null"
            class="text-secondary text-xs"
          >
            <template v-if="customerState?.emailMessagesSent">
              Last sent {{ dayjs(customerState.emailLastSentTimestamp).fromNow() }}
            </template>
            <template v-else>
              No emails sent
            </template>
          </div>
        </div>
      </div>
      <div class="flex flex-row items-start justify-between ">
        <div class="flex-shrink basis-[110px] pr-4 text-secondary w-28 ">
          Engagement
        </div>
        <div
          v-if="isLoading"
          class="flex-1 h-8"
        >
          <AM3LoadingContent :lines="1" />
        </div>

        <div
          v-else
          class="flex-1"
        >
          <div v-if="hasEmailEngagement">
            <div v-if="emailOpenRateLabel">
              {{ emailOpenRateLabel }}
            </div>
            <div v-if="emailClickRateLabel">
              {{ emailClickRateLabel }}
            </div>
          </div>
          <div v-else>
            No Engagement Yet
          </div>
        </div>
      </div>
    </div>

    <!-- SMS Stats -->
    <div class="rounded-md bg-surfaces-neutral p-4 mb-4 [&_[data-component=am3-tag]]:outline-0">
      <div class="flex flex-row items-center justify-between mb-3">
        <h3 class="font-medium leading-tight">
          SMS
          <span class="block text-secondary text-xs font-normal mt-0.5">Last {{ customerState?.summaryPeriodDays || '90' }} days</span>
        </h3>
        <AM3Tag
          :type="getSubscriptionType(smsSubscribed)"
          :label="getSubscriptionLabel(smsSubscribed)"
          :dot="true"
        />
      </div>
      <div class="flex flex-row items-start justify-between mb-4">
        <div class="flex-shrink basis-[110px] pr-4 text-secondary w-28 ">
          Sent
        </div>

        <div
          v-if="isLoading"
          class="flex-1 h-8"
        >
          <AM3LoadingContent :lines="1" />
        </div>

        <div
          v-else
          class="flex-1"
        >
          <div>
            {{ customerState?.smsMessagesSent || '0' }} SMS
          </div>
          <div
            v-tooltip.bottom="customerState?.smsMessagesSent && customerState?.smsLastSentTimestamp ? formatDateTime(customerState.smsLastSentTimestamp) : null"
            class="text-secondary text-xs"
          >
            <template v-if="customerState?.smsMessagesSent">
              Last sent {{ dayjs(customerState.smsLastSentTimestamp).fromNow() }}
            </template>
            <template v-else>
              No SMS sent
            </template>
          </div>
        </div>
      </div>
      <div class="flex flex-row items-start justify-between ">
        <div class="flex-shrink basis-[110px] pr-4 text-secondary w-28 ">
          Engagement
        </div>
        <div
          v-if="isLoading"
          class="flex-1 h-8"
        >
          <AM3LoadingContent :lines="1" />
        </div>

        <div
          v-else
          class="flex-1"
        >
          <div>
            {{ smsStats }}
          </div>
        </div>
      </div>
    </div>
  </AM3DetailCard>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import dayjs from 'dayjs'
import AM3DetailCard from '@/components/am3/detail-card/AM3DetailCard.vue'
import AM3Tag from '@/components/am3-generic/base/AM3Tag.vue'
import AM3LoadingContent from '@/components/am3-generic/base/AM3LoadingContent.vue'

import { formatDateTime } from '@/utils/formatters'

interface CustomerMessageSummary {
  oid: number
  fanOid: number
  promoterOid: number
  emailMessagesSent: number
  emailMessagesOpened: number
  emailMessagesClicked: number
  emailLastSentTimestamp: string | null
  emailOpenRate: number
  emailClickRate: number
  smsMessagesSent: number
  smsMessagesClicked: number
  smsLastSentTimestamp: string | null
  smsClickRate: number
  summaryPeriodDays: number
  lastComputedTimestamp: string
  sysCtime: string
  sysMtime: string
}

// MFPAM all-time, channel-combined totals (see EC-3953).
interface CustomerMessageTotals {
  totalMessagesSent: number
  totalMessagesOpened: number
  totalMessagesClicked: number
  lastMessageSent: string | null
}

// Click rate is meaningful combined across channels (both email and SMS register
// clicks). Open rate is omitted here because SMS doesn't track opens, which would
// deflate the combined rate misleadingly — open rate stays on the per-channel rows.
const combinedClickRate = computed(() => {
  const sent = props.customerTotals?.totalMessagesSent
  const clicked = props.customerTotals?.totalMessagesClicked
  if (!sent || !clicked) return 'No engagement yet'
  return `${((clicked / sent) * 100).toFixed(0)}% click rate`
})

const props = defineProps<{
  isLoading?: boolean
  isLoadingTotals?: boolean
  customerState?: CustomerMessageSummary | null
  customerTotals?: CustomerMessageTotals | null
  emailSubscribed?: boolean
  smsSubscribed?: boolean
}>()

const emit = defineEmits<{
  'manage-subscriptions': []
}>()

const handleManageListSubscriptions = () => {
  emit('manage-subscriptions')
}

// open and click rates for emails — split into two computed properties so the
// template can stack them on separate lines instead of wrapping awkwardly.
const emailOpenRateLabel = computed(() => {
  if (!props.customerState?.emailOpenRate) return null
  return `${(props.customerState.emailOpenRate * 100).toFixed(0)}% open rate`
})
const emailClickRateLabel = computed(() => {
  if (!props.customerState?.emailClickRate) return null
  return `${(props.customerState.emailClickRate * 100).toFixed(0)}% click rate`
})
const hasEmailEngagement = computed(
  () => Boolean(emailOpenRateLabel.value || emailClickRateLabel.value),
)

// percentage stats for SMS
const smsStats = computed(() => {
  if (props.customerState?.smsClickRate) {
    return props.customerState.smsClickRate ? `${(props.customerState.smsClickRate * 100).toFixed(0)}%` : `0%`
  }
  else {
    return 'No Engagement Yet'
  }
})

// Helper functions for subscription states
const getSubscriptionType = (subscribed?: boolean) => {
  if (subscribed === true) return 'subscribed'
  if (subscribed === false) return 'unsubscribed'
  return 'secondary' // gray for not subscribed yet
}

const getSubscriptionLabel = (subscribed?: boolean) => {
  if (subscribed === true) return 'Opted in'
  if (subscribed === false) return 'Opted out'
  return 'Not currently opted in'
}
</script>
